import { Heading, Icon, Modal, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "styled-system/jsx";
import { ArrowLeft, ArrowRight, Mail, MailOpen } from "iconoir-react";
import { ActivityLogManyQuery, EnumActivityLogType } from "gql/generated";
import { formatDate } from "shared/utils";
import { useMemo, useState, useEffect } from "react";

type EmailActivitySlideoutProps = {
  isOpen: boolean;
  onClose: () => void;
  activityLog: ActivityLogManyQuery["activityLogMany"][0] | null;
  onOpenPerson?: (personId: string) => void;
  /** Stacking override when opened above another slideout/modal (e.g. a flyout). */
  zIndex?: "medium" | "max";
};

/** Timeline: Sent, then Delivered, Opened, Clicked, and failure events (Bounced, Dropped, Deferred, Spam) */
function EmailRecipientTimeline({
  sentAt,
  deliveredAt,
  openedAt,
  clickedAt,
  bouncedAt,
  droppedAt,
  deferredAt,
  spamReportedAt,
}: {
  sentAt?: string | null;
  deliveredAt?: string | null;
  openedAt?: string | null;
  clickedAt?: string | null;
  bouncedAt?: string | null;
  droppedAt?: string | null;
  deferredAt?: string | null;
  spamReportedAt?: string | null;
}) {
  const { t } = useTranslation("home", { keyPrefix: "VIEWS.ACTIVITY_FEED" });
  const items = useMemo(() => {
    const list: { label: string; date: string | null; isFailure?: boolean }[] = [];
    list.push({
      label: t("EMAIL_SENT") ?? "Sent",
      date: sentAt ?? null,
    });
    if (deliveredAt) {
      list.push({
        label: t("EMAIL_RECEIVED") ?? "Delivered",
        date: deliveredAt,
      });
    }
    if (openedAt) {
      list.push({
        label: t("EMAIL_OPENED") ?? "Opened",
        date: openedAt,
      });
    }
    if (clickedAt) {
      list.push({
        label: t("EMAIL_CLICKED") ?? "Clicked",
        date: clickedAt,
      });
    }
    if (bouncedAt) {
      list.push({
        label: t("EMAIL_BOUNCED") ?? "Bounced",
        date: bouncedAt,
        isFailure: true,
      });
    }
    if (droppedAt) {
      list.push({
        label: t("EMAIL_DROPPED") ?? "Dropped",
        date: droppedAt,
        isFailure: true,
      });
    }
    if (deferredAt) {
      list.push({
        label: t("EMAIL_DEFERRED") ?? "Deferred",
        date: deferredAt,
        isFailure: true,
      });
    }
    if (spamReportedAt) {
      list.push({
        label: t("EMAIL_SPAM_REPORTED") ?? "Spam reported",
        date: spamReportedAt,
        isFailure: true,
      });
    }
    return list;
  }, [sentAt, deliveredAt, openedAt, clickedAt, bouncedAt, droppedAt, deferredAt, spamReportedAt, t]);

  return (
    <Box
      border="1px solid"
      borderColor="border.light"
      borderRadius="100"
      p="250"
      mb="150"
    >
      <Flex flexDir="column" gap="100">
        {items.map((item, i) => (
          <Flex key={i} alignItems="center" gap="150">
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={
                item.date
                  ? item.isFailure
                    ? "icon.error.mid"
                    : "icon.success.mid"
                  : "border.light"
              }
              flexShrink={0}
            />
            <Text visual="smallRegular" color="text.dark">
              {item.label}
              {item.date ? ` · ${formatDate(item.date, "PPp")}` : ""}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}

/** Person card styled like ImportRuns People (clickable card with arrow) */
function PersonCard({
  displayName,
  email,
  onClick,
}: {
  displayName: string;
  email?: string | null;
  onClick: () => void;
}) {
  return (
    <Box
      p="200"
      border="1px solid"
      borderColor="border.light"
      borderRadius="100"
      bgColor="bg.wa.lightest"
      cursor="pointer"
      _hover={{
        bgColor: "bg.wa.light",
        borderColor: "border.mid",
      }}
      onClick={onClick}
    >
      <Flex alignItems="center" justifyContent="space-between" gap="200">
        <Flex flexDir="column" gap="50" flex="1">
          <Text visual="bodySemiBold" color="text.dark">
            {displayName}
          </Text>
          {email && (
            <Text visual="smallRegular" color="text.mid">
              {email}
            </Text>
          )}
        </Flex>
        <Icon Element={ArrowRight} size="sm" color="icon.mid" />
      </Flex>
    </Box>
  );
}

export const EmailActivitySlideout = ({
  isOpen,
  onClose,
  activityLog,
  onOpenPerson,
  zIndex,
}: EmailActivitySlideoutProps) => {
  const { t } = useTranslation("home", { keyPrefix: "VIEWS.ACTIVITY_FEED" });
  const [selectedRecipientIndex, setSelectedRecipientIndex] = useState<
    number | null
  >(null);

  const typeStr = String(activityLog?.type ?? "");
  const isDigest =
    activityLog?.type === EnumActivityLogType.EmailDailyDigestSent ||
    typeStr === "EMAIL_DAILY_DIGEST_SENT";
  const isBroadcastNotification =
    typeStr === "EMAIL_BROADCAST_NOTIFICATION_SENT";
  const isRenewal =
    activityLog?.type === EnumActivityLogType.EmailRenewalSent ||
    typeStr === "EMAIL_RENEWAL_SENT";
  const isPaymentRequest =
    activityLog?.type === EnumActivityLogType.EmailPaymentRequestSent ||
    typeStr === "EMAIL_PAYMENT_REQUEST_SENT";

  const recipients = (activityLog?.emailActivity?.recipients ?? []).filter(
    (r): r is NonNullable<typeof r> => r != null,
  );
  const hasEmailActivity = !!activityLog?.emailActivity;
  const html =
    activityLog?.emailActivity?.sentHtml ??
    activityLog?.emailActivity?.templatePreviewHtml;
  const fromAddress = activityLog?.emailActivity?.fromAddress;
  const subject = activityLog?.emailActivity?.subject;
  const timeSent = activityLog?.createdDateISO;

  const hasMultipleRecipients = recipients.length > 1;
  const showIndexView =
    hasEmailActivity && hasMultipleRecipients && selectedRecipientIndex === null;
  const showDetailView =
    hasEmailActivity &&
    (!hasMultipleRecipients || selectedRecipientIndex !== null);
  const detailRecipientIndex =
    hasMultipleRecipients && selectedRecipientIndex !== null
      ? selectedRecipientIndex
      : 0;
  const detailRecipient = recipients[detailRecipientIndex] ?? null;
  const toAddresses = detailRecipient
    ? detailRecipient.email
    : recipients.map(r => r.email).filter(Boolean).join(", ");
  const fromDisplay =
    fromAddress?.name && fromAddress?.email
      ? `${fromAddress.name} <${fromAddress.email}>`
      : fromAddress?.email ?? "";

  const typeLabel = isDigest
    ? (t("EMAIL_DAILY_DIGEST_TITLE") as string) || "Daily Digest"
    : isBroadcastNotification
      ? (t("EMAIL_BROADCAST_NOTIFICATION_TITLE") as string) || "Broadcast notification"
      : isRenewal
        ? (t("EMAIL_RENEWAL_TITLE") as string) || "Membership renewal email"
        : isPaymentRequest
          ? (t("EMAIL_PAYMENT_REQUEST_TITLE") as string) || "Invoice email"
          : (t("EMAIL_INVITE_TITLE") as string) || "Invite";
  const hasBeenOpenedOrReceived = recipients.some(
    (r) => !!r.deliveredAt || !!r.openedAt
  );
  const MailIcon = hasBeenOpenedOrReceived ? MailOpen : Mail;

  const fallbackMessage =
    activityLog?.message &&
    typeof activityLog.message === "object" &&
    "en" in activityLog.message
      ? (activityLog.message as { en?: string | null }).en ??
        (activityLog.message as { fr?: string | null }).fr ??
        (activityLog.message as { es?: string | null }).es
      : null;

  useEffect(() => {
    if (!isOpen || !activityLog) setSelectedRecipientIndex(null);
  }, [isOpen, activityLog?._id]);

  if (!activityLog) return null;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) {
          setSelectedRecipientIndex(null);
          onClose();
        }
      }}
      closeTriggerVisual="light"
      portal
      {...(zIndex ? { zIndex } : {})}
    >
      <Flex
        flexDir="column"
        h="full"
        minH="100vh"
        overflow="hidden"
        w="full"
        maxW="1000px"
        bg="bg.wa.light"
      >
        <Flex
          alignItems="center"
          gap="200"
          p="400"
          borderBottom="1px solid"
          borderColor="border.light"
        >
          <Box fontSize="inherit" lineHeight="1" display="flex" alignItems="center">
            <Icon Element={MailIcon} size="lg" />
          </Box>
          <Heading visual="h4">{typeLabel}</Heading>
        </Flex>
        <Flex
          flex="1"
          minH="200px"
          overflow="auto"
          flexDir="column"
          gap="200"
          p="400"
        >
          {showDetailView && hasMultipleRecipients && (
            <button
              type="button"
              aria-label="Back to recipients"
              onClick={() => setSelectedRecipientIndex(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 0",
                alignSelf: "flex-start",
                background: "none",
                border: "none",
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
              }}
            >
              <Icon Element={ArrowLeft} size="sm" />
              <Text visual="smallMedium" color="text.mid">
                {(t("EMAIL_BACK_TO_EMAILS") as string) || "Back to emails"}
              </Text>
            </button>
          )}
          {!hasEmailActivity && (
            <Box p="400" bg="bg.wa.light" borderRadius="100">
              <Text visual="bodyMedium" color="text.mid">
                {fallbackMessage ??
                  (t("EMAIL_NO_DETAILS") as string) ??
                  "No email details available. Open this from Activity Logs in the sidebar to see recipients and preview."}
              </Text>
            </Box>
          )}

          {/* Index view: when the email was sent + list of recipients; click opens detail for that recipient */}
          {showIndexView && (
            <Box>
              {timeSent && (
                <Box mb="300">
                  <Text visual="smallTag" color="text.light" mb="200">
                    {(t("EMAIL_TIME_SENT") as string) ?? "Time sent"}
                  </Text>
                  <Text visual="bodyMedium" color="text.dark">
                    {formatDate(timeSent, "PPp")}
                  </Text>
                </Box>
              )}
              <Text visual="smallTag" color="text.light" mb="200">
                {(t("EMAIL_EMAILS") as string) || "Emails"}
              </Text>
              <Flex flexDir="column" gap="150">
                {recipients.map((r, i) => (
                  <PersonCard
                    key={r.personId ?? r.email ?? i}
                    displayName={r.displayName ?? r.email ?? ""}
                    email={r.email}
                    onClick={() => setSelectedRecipientIndex(i)}
                  />
                ))}
              </Flex>
            </Box>
          )}

          {/* Detail view: single recipient's timeline + recipient card (optional person link) + email preview */}
          {showDetailView && detailRecipient && (
            <Box>
              <Text visual="smallTag" color="text.light" mb="200">
                {(t("EMAIL_ACTIVITY_LOG") as string) || "Activity Log"}
              </Text>
              <EmailRecipientTimeline
                sentAt={timeSent ?? undefined}
                deliveredAt={detailRecipient.deliveredAt}
                openedAt={detailRecipient.openedAt}
                clickedAt={detailRecipient.clickedAt}
                bouncedAt={detailRecipient.bouncedAt}
                droppedAt={detailRecipient.droppedAt}
                deferredAt={detailRecipient.deferredAt}
                spamReportedAt={detailRecipient.spamReportedAt}
              />
              {onOpenPerson && detailRecipient.personId && (
                <Box mt="200">
                  <Text visual="smallTag" color="text.light" mb="200">
                    {(t("EMAIL_RECIPIENT") as string) || "Recipient"}
                  </Text>
                  <PersonCard
                    displayName={detailRecipient.displayName ?? detailRecipient.email ?? ""}
                    email={detailRecipient.email}
                    onClick={() => onOpenPerson(detailRecipient.personId!)}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Email preview — only in detail view; show metadata always, body when html is stored */}
          {showDetailView && hasEmailActivity && (
            <Box flex="1" minH="0" display="flex" flexDir="column">
              <Text visual="smallTag" color="text.light" mb="200">
                {t("EMAIL_PREVIEW") ?? "Email preview"}
              </Text>
              {/* From, To, Subject, Time sent */}
              <Box
                mb="300"
                p="300"
                border="1px solid"
                borderColor="border.light"
                borderRadius="100"
                bg="white"
              >
                <Flex flexDir="column" gap="150">
                  {fromDisplay ? (
                    <Flex gap="150" alignItems="baseline">
                      <Text visual="smallMedium" color="text.light" w="80px" flexShrink={0}>
                        {(t("EMAIL_FROM") as string) ?? "From"}
                      </Text>
                      <Text visual="smallRegular" color="text.dark">{fromDisplay}</Text>
                    </Flex>
                  ) : null}
                  {toAddresses ? (
                    <Flex gap="150" alignItems="baseline">
                      <Text visual="smallMedium" color="text.light" w="80px" flexShrink={0}>
                        {(t("EMAIL_TO") as string) ?? "To"}
                      </Text>
                      <Text visual="smallRegular" color="text.dark">{toAddresses}</Text>
                    </Flex>
                  ) : null}
                  {subject ? (
                    <Flex gap="150" alignItems="baseline">
                      <Text visual="smallMedium" color="text.light" w="80px" flexShrink={0}>
                        {(t("EMAIL_SUBJECT") as string) ?? "Subject"}
                      </Text>
                      <Text visual="smallRegular" color="text.dark">{subject}</Text>
                    </Flex>
                  ) : null}
                  {timeSent ? (
                    <Flex gap="150" alignItems="baseline">
                      <Text visual="smallMedium" color="text.light" w="80px" flexShrink={0}>
                        {(t("EMAIL_TIME_SENT") as string) ?? "Time sent"}
                      </Text>
                      <Text visual="smallRegular" color="text.dark">
                        {formatDate(timeSent, "PPp")}
                      </Text>
                    </Flex>
                  ) : null}
                </Flex>
              </Box>
              {/* Rendered email body when available; otherwise placeholder */}
              {html ? (
                (() => {
                  const trimStyle =
                    "<style>html,body{margin:0;} body{padding:32px 40px;} body{padding-bottom:0 !important;margin-bottom:0 !important;} body *:last-child{margin-bottom:0 !important;}</style>";
                  const htmlToShow = html.includes("</head>")
                    ? html.replace("</head>", `${trimStyle}</head>`)
                    : trimStyle + html;
                  return (
                    <Box
                      flex="1"
                      minH="200px"
                      border="1px solid"
                      borderColor="border.light"
                      borderRadius="100"
                      overflow="hidden"
                      bg="white"
                      display="flex"
                      flexDir="column"
                    >
                      <iframe
                        title="Email preview"
                        srcDoc={htmlToShow}
                        sandbox="allow-same-origin"
                        style={{
                          width: "100%",
                          flex: 1,
                          minHeight: "200px",
                          border: "none",
                          display: "block",
                        }}
                      />
                    </Box>
                  );
                })()
              ) : (
                <Box
                  p="400"
                  border="1px solid"
                  borderColor="border.light"
                  borderRadius="100"
                  bg="white"
                >
                  <Text visual="smallRegular" color="text.mid">
                    {(t("EMAIL_PREVIEW_NOT_AVAILABLE") as string) ??
                      "Email preview not available for this message."}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};
